import { TemplateSlot } from '../hooks/useTemplates';

export interface SceneGroup {
  id: string;
  label: string;
  slots: TemplateSlot[];
}

/** Derive a human-readable label from a raw scene_id string. */
function sceneLabel(sceneId: string): string {
  return sceneId
    // "scene1" → "scene 1"
    .replace(/([a-z])(\d)/g, '$1 $2')
    // underscores/hyphens → spaces
    .replace(/[_-]/g, ' ')
    // title-case every word
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Groups template slots into scenes using slot metadata.
 *
 * Grouping key priority:
 *   1. slot.scene_id          (direct field on the slot)
 *   2. slot.map_from?.scene_id (pipeline mapping metadata)
 *   3. 'Other'               (fallback — works for any template)
 *
 * Groups are returned in the order they are first encountered, so the
 * scene order matches the slot order defined in the template document.
 * This makes the function template-agnostic: no code change is required
 * when a new template is added to Firestore.
 */
export function groupSlotsByScene(slots: TemplateSlot[]): SceneGroup[] {
  const groupMap = new Map<string, SceneGroup>();

  for (const slot of slots) {
    const sceneId = slot.scene_id ?? slot.map_from?.scene_id ?? 'Other';
    if (!groupMap.has(sceneId)) {
      groupMap.set(sceneId, {
        id: sceneId,
        label: sceneId === 'Other' ? 'Other' : sceneLabel(sceneId),
        slots: [],
      });
    }
    const group = groupMap.get(sceneId);
    if (group) group.slots.push(slot);
  }

  return Array.from(groupMap.values());
}
